"use client"

import { useState, useEffect, useCallback } from "react"
import { Link } from "react-router-dom"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faUsers,
  faUserCheck,
  faUserClock,
  faMoneyBillWave,
  faSpinner,
  faUserPlus,
  faTags,
  faExclamationTriangle,
  faCheckCircle,
  faQrcode,
  faEdit,
  faSave,
  faTimes,
  faUpload,
  faTrash,
  faEye,
  faCheck,
  faBan,
  faClock,
  faReceipt,
  faCreditCard,
} from "@fortawesome/free-solid-svg-icons"
import { api, uploadQRCodeImage, removeQRCodeImage } from "../utils/auth"

const AdminDashboard = () => {
  const [metrics, setMetrics] = useState({
    totalUsers: 0,
    activeSubscriptions: 0,
    trialUsers: 0,
    monthlyRevenue: 0,
    transactionStats: {
      totalTransactions: 0,
      pendingTransactions: 0,
      verifiedTransactions: 0,
      rejectedTransactions: 0,
      totalAmount: 0,
      averageAmount: 0,
    },
  })
  const [usersData, setUsersData] = useState([])
  const [loadingMetrics, setLoadingMetrics] = useState(true)
  const [loadingUsers, setLoadingUsers] = useState(false)
  const [error, setError] = useState(null)
  const [successMessage, setSuccessMessage] = useState(null)
  const [showUsersTable, setShowUsersTable] = useState(false)
  const [actionLoading, setActionLoading] = useState({})

  // Payment Settings State
  const [paymentSettings, setPaymentSettings] = useState({
    upiId: "merchant@paytm",
    paypalClientId: "",
    qrCodeUrl: "",
    isActive: true,
  })
  const [showPaymentSettings, setShowPaymentSettings] = useState(false)
  const [editingPaymentSettings, setEditingPaymentSettings] = useState(false)
  const [tempPaymentSettings, setTempPaymentSettings] = useState({})
  const [loadingPaymentSettings, setLoadingPaymentSettings] = useState(false)
  const [uploadingQR, setUploadingQR] = useState(false)

  // Pricing Settings State
  const [pricingSettings, setPricingSettings] = useState([])
  const [showPricingSettings, setShowPricingSettings] = useState(false)
  const [editingPricingSettings, setEditingPricingSettings] = useState(false)
  const [tempPricingSettings, setTempPricingSettings] = useState([])
  const [loadingPricingSettings, setLoadingPricingSettings] = useState(false)

  // Transaction Management State
  const [selectedUserTransactions, setSelectedUserTransactions] = useState(null)
  const [showTransactionModal, setShowTransactionModal] = useState(false)
  const [updatingTransaction, setUpdatingTransaction] = useState(false)

  const fetchMetrics = useCallback(async () => {
    setLoadingMetrics(true)
    setError(null)

    try {
      const response = await api.get("/admin/dashboard")
      console.log("Metrics API response:", response)

      if (response && response.data) {
        console.log("Setting metrics from API:", response.data)
        setMetrics({
          totalUsers: response.data.totalUsers || 0,
          activeSubscriptions: response.data.activeSubscriptions || 0,
          trialUsers: response.data.trialUsers || 0,
          monthlyRevenue: response.data.monthlyRevenue || 0,
          transactionStats: response.data.transactionStats || {
            totalTransactions: 0,
            pendingTransactions: 0,
            verifiedTransactions: 0,
            rejectedTransactions: 0,
            totalAmount: 0,
            averageAmount: 0,
          },
        })
      } else {
        throw new Error("Invalid response format")
      }
    } catch (err) {
      console.error("Error fetching metrics:", err)
      setError("Failed to fetch dashboard metrics. Please try again later.")

      // Use mock data as fallback
      console.log("Using mock metrics data")
      setMetrics({
        totalUsers: 256,
        activeSubscriptions: 187,
        trialUsers: 42,
        monthlyRevenue: 15680,
        transactionStats: {
          totalTransactions: 45,
          pendingTransactions: 12,
          verifiedTransactions: 28,
          rejectedTransactions: 5,
          totalAmount: 25000,
          averageAmount: 555.56,
        },
      })
    } finally {
      setLoadingMetrics(false)
    }
  }, [])

  const fetchPaymentSettings = useCallback(async () => {
    setLoadingPaymentSettings(true)
    setError(null)

    try {
      const response = await api.get("/admin/payment-settings")
      console.log("Payment settings API response:", response)

      if (response && response.data) {
        setPaymentSettings(response.data)
      } else {
        throw new Error("Invalid payment settings response format")
      }
    } catch (err) {
      console.error("Error fetching payment settings:", err)
      // Use default settings as fallback
      setPaymentSettings({
        upiId: "merchant@paytm",
        paypalClientId: "",
        qrCodeUrl: "",
        isActive: true,
      })
    } finally {
      setLoadingPaymentSettings(false)
    }
  }, [])

  // Fetch Pricing Settings
  const fetchPricingSettings = useCallback(async () => {
    setLoadingPricingSettings(true)
    setError(null)

    try {
      const response = await api.get("/admin/pricing-settings")
      console.log("Pricing settings API response:", response)

      if (response && response.data && response.data.plans) {
        setPricingSettings(response.data.plans)
      } else {
        throw new Error("Invalid pricing settings response format")
      }
    } catch (err) {
      console.error("Error fetching pricing settings:", err)
      // Use default/mock pricing data as fallback
      setPricingSettings([
        {
          name: "Free",
          price: { monthly: 0, yearly: 0 },
          description: "Perfect for testing and small projects",
          features: [
            "Export up to 50 products per day",
            "Basic product information",
            "CSV export format",
            "Email support",
          ],
          buttonText: "Start Free",
          buttonLink: "/home",
          highlighted: false,
        },
        {
          name: "Professional",
          price: { monthly: 14.99, yearly: 140 },
          description: "Ideal for agencies and growing businesses",
          features: [
            "Export unlimited products",
            "Complete product data",
            "Multiple export formats",
            "Priority support",
          ],
          buttonText: "Go Professional",
          buttonLink: "/payments?plan=professional&amount=14.99&billing=monthly",
          highlighted: true,
        },
        {
          name: "Business",
          price: { monthly: 29.99, yearly: 300 },
          description: "For large-scale operations and enterprises",
          features: ["Everything in Professional", "Unlimited stores", "Dedicated support", "Custom integrations"],
          buttonText: "Contact Us",
          buttonLink: "/contact",
          highlighted: false,
        },
      ])
    } finally {
      setLoadingPricingSettings(false)
    }
  }, [])

  const fetchUsers = useCallback(async () => {
    if (showUsersTable) {
      setLoadingUsers(true)
      setError(null)

      try {
        console.log("Fetching users from /admin/users")
        const response = await api.get("/admin/users")
        console.log("Users API response:", response)

        if (response && response.data) {
          console.log("Setting users data from API:", response.data)
          setUsersData(response.data)
        } else {
          throw new Error("Invalid users response format")
        }
      } catch (err) {
        console.error("Error fetching users:", err)
        setError("Failed to fetch user data. Please try again later.")

        // Use mock data as fallback
        console.log("Using mock user data")
        setUsersData([
          {
            id: 1,
            name: "John Doe",
            email: "john@example.com",
            role: "user",
            plan_type: "professional",
            transactions: [],
            totalTransactions: 2,
            pendingTransactions: 1,
            verifiedTransactions: 1,
            totalPaid: 29.99,
            lastTransaction: {
              id: 1,
              transaction_id: "TXN123456",
              amount: 29.99,
              status: "verified",
              submitted_at: "2024-01-15T10:30:00Z",
            },
          },
          {
            id: 2,
            name: "Jane Smith",
            email: "jane@example.com",
            role: "user",
            plan_type: "business",
            transactions: [],
            totalTransactions: 1,
            pendingTransactions: 1,
            verifiedTransactions: 0,
            totalPaid: 0,
            lastTransaction: {
              id: 2,
              transaction_id: "TXN789012",
              amount: 19.99,
              status: "pending",
              submitted_at: "2024-01-16T14:20:00Z",
            },
          },
        ])
      } finally {
        setLoadingUsers(false)
      }
    }
  }, [showUsersTable])

  useEffect(() => {
    fetchMetrics()
    fetchPaymentSettings()
    fetchPricingSettings()
  }, [fetchMetrics, fetchPaymentSettings, fetchPricingSettings])

  useEffect(() => {
    if (showUsersTable) {
      fetchUsers()
    }
  }, [showUsersTable, fetchUsers])

  const handleManageUsersClick = () => {
    setShowUsersTable((prev) => !prev)
    setShowPaymentSettings(false)
    setShowPricingSettings(false)
  }

  const handlePaymentSettingsClick = () => {
    setShowPaymentSettings((prev) => !prev)
    setShowUsersTable(false)
    setShowPricingSettings(false)
  }

  const handlePricingSettingsClick = () => {
    setShowPricingSettings((prev) => !prev)
    setShowUsersTable(false)
    setShowPaymentSettings(false)
  }

  const handleEditPaymentSettings = () => {
    setTempPaymentSettings({ ...paymentSettings })
    setEditingPaymentSettings(true)
  }

  const handleCancelEditPaymentSettings = () => {
    setTempPaymentSettings({})
    setEditingPaymentSettings(false)
  }

  const handleSavePaymentSettings = async () => {
    setLoadingPaymentSettings(true)
    setError(null)
    setSuccessMessage(null)

    try {
      const response = await api.put("/admin/payment-settings", {
        upiId: tempPaymentSettings.upiId,
        paypalClientId: tempPaymentSettings.paypalClientId || "",
        isActive: tempPaymentSettings.isActive,
      })

      if (response && response.data) {
        setPaymentSettings(response.data)
        setEditingPaymentSettings(false)
        setTempPaymentSettings({})
        showSuccess("Payment settings updated successfully!")
      } else {
        throw new Error("Invalid response format")
      }
    } catch (err) {
      console.error("Error updating payment settings:", err)
      setError("Failed to update payment settings. Please try again.")
    } finally {
      setLoadingPaymentSettings(false)
    }
  }

  // Handle Edit Pricing Settings
  const handleEditPricingSettings = () => {
    setTempPricingSettings([...pricingSettings])
    setEditingPricingSettings(true)
  }

  // Handle Cancel Edit Pricing Settings
  const handleCancelEditPricingSettings = () => {
    setTempPricingSettings([])
    setEditingPricingSettings(false)
  }

  // Handle Save Pricing Settings
  const handleSavePricingSettings = async () => {
    setLoadingPricingSettings(true)
    setError(null)
    setSuccessMessage(null)

    try {
      console.log("Saving pricing settings:", tempPricingSettings)

      const response = await api.put("/admin/pricing-settings", tempPricingSettings)

      if (response && response.data && response.data.plans) {
        setPricingSettings(response.data.plans)
        setEditingPricingSettings(false)
        setTempPricingSettings([])
        showSuccess("Pricing settings updated successfully!")
      } else {
        throw new Error("Invalid response format")
      }
    } catch (err) {
      console.error("Error updating pricing settings:", err)
      setError(err.response?.data?.error || "Failed to update pricing settings. Please try again.")
    } finally {
      setLoadingPricingSettings(false)
    }
  }

  // Handle price change for a specific plan
  const handlePriceChange = (planIndex, billingType, value) => {
    const updatedPlans = [...tempPricingSettings]
    const numericValue = Number.parseFloat(value) || 0
    updatedPlans[planIndex].price[billingType] = numericValue
    setTempPricingSettings(updatedPlans)
  }

  const handleQRCodeUpload = async (event) => {
    const file = event.target.files[0]
    if (!file) return

    // Validate file type
    const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "image/gif", "image/webp"]
    if (!allowedTypes.includes(file.type)) {
      setError("Invalid file type. Only JPEG, PNG, GIF, and WebP images are allowed.")
      return
    }

    // Validate file size (5MB limit)
    if (file.size > 5 * 1024 * 1024) {
      setError("File size too large. Maximum size is 5MB.")
      return
    }

    setUploadingQR(true)
    setError(null)
    setSuccessMessage(null)

    try {
      const response = await uploadQRCodeImage(file)

      if (response && response.settings) {
        setPaymentSettings(response.settings)
        showSuccess("QR code image uploaded successfully!")
      } else {
        throw new Error("Invalid response format")
      }
    } catch (err) {
      console.error("Error uploading QR code:", err)
      setError(err.response?.data?.error || "Failed to upload QR code image. Please try again.")
    } finally {
      setUploadingQR(false)
      // Reset file input
      event.target.value = ""
    }
  }

  const handleRemoveQRCode = async () => {
    if (!window.confirm("Are you sure you want to remove the QR code image?")) {
      return
    }

    setLoadingPaymentSettings(true)
    setError(null)
    setSuccessMessage(null)

    try {
      const response = await removeQRCodeImage()

      if (response && response.settings) {
        setPaymentSettings(response.settings)
        showSuccess("QR code image removed successfully!")
      } else {
        throw new Error("Invalid response format")
      }
    } catch (err) {
      console.error("Error removing QR code:", err)
      setError("Failed to remove QR code image. Please try again.")
    } finally {
      setLoadingPaymentSettings(false)
    }
  }

  const showSuccess = (message) => {
    setSuccessMessage(message)
    setError(null)
    setTimeout(() => setSuccessMessage(null), 3000)
  }

  const handleUpgradePlanChange = useCallback(
    async (userId, newPlan) => {
      setActionLoading((prev) => ({ ...prev, [userId]: true }))
      setError(null)
      setSuccessMessage(null)

      console.log(`Attempting to upgrade user ${userId} to plan: ${newPlan}`)

      try {
        const url = `/admin/users/${userId}/upgrade-plan`
        console.log("Making PUT request to:", url)

        const response = await api.put(url, { newPlan })

        console.log("Upgrade plan response:", response.data)

        setUsersData((prevUsers) =>
          prevUsers.map((user) => (user.id === userId ? { ...user, plan_type: newPlan } : user)),
        )

        showSuccess(`Successfully upgraded user to ${newPlan || "Free"} plan`)
        fetchMetrics()
      } catch (err) {
        console.error(`Error upgrading plan for user ${userId}:`, err)

        const errorMessage = err.response?.data?.message || err.message || "Failed to upgrade plan"
        setError(`Error: ${errorMessage}`)

        console.log("Error details:", {
          status: err.response?.status,
          statusText: err.response?.statusText,
          data: err.response?.data,
          headers: err.response?.headers,
        })
      } finally {
        setActionLoading((prev) => ({ ...prev, [userId]: false }))
      }
    },
    [fetchMetrics],
  )

  const handleCancelSubscriptionClick = useCallback(
    async (userId) => {
      setActionLoading((prev) => ({ ...prev, [userId]: true }))
      setError(null)
      setSuccessMessage(null)

      console.log(`Attempting to cancel subscription for user ${userId}`)

      try {
        const url = `/admin/users/${userId}/cancel-subscription`
        console.log("Making PUT request to:", url)

        const response = await api.put(url)

        console.log("Cancel subscription response:", response.data)

        setUsersData((prevUsers) => prevUsers.map((user) => (user.id === userId ? { ...user, plan_type: null } : user)))

        showSuccess("Successfully canceled subscription")
        fetchMetrics()
      } catch (err) {
        console.error(`Error cancelling subscription for user ${userId}:`, err)

        const errorMessage = err.response?.data?.message || err.message || "Failed to cancel subscription"
        setError(`Error: ${errorMessage}`)

        console.log("Error details:", {
          status: err.response?.status,
          statusText: err.response?.statusText,
          data: err.response?.data,
          headers: err.response?.headers,
        })
      } finally {
        setActionLoading((prev) => ({ ...prev, [userId]: false }))
      }
    },
    [fetchMetrics],
  )

  const handleViewTransactions = (user) => {
    setSelectedUserTransactions(user)
    setShowTransactionModal(true)
  }

  const handleUpdateTransactionStatus = async (transactionId, newStatus, notes = "") => {
    setUpdatingTransaction(true)
    setError(null)
    setSuccessMessage(null)

    try {
      const response = await api.put(`/admin/transactions/${transactionId}/status`, {
        status: newStatus,
        notes: notes,
      })

      if (response && response.data) {
        // Update the transaction in the selected user's transactions
        setSelectedUserTransactions((prev) => ({
          ...prev,
          transactions: prev.transactions.map((t) =>
            t.id === transactionId ? { ...t, status: newStatus, notes: notes } : t,
          ),
        }))

        // Refresh users data to update counts
        fetchUsers()
        fetchMetrics()

        showSuccess(`Transaction ${newStatus} successfully!`)
      }
    } catch (err) {
      console.error("Error updating transaction status:", err)
      setError("Failed to update transaction status. Please try again.")
    } finally {
      setUpdatingTransaction(false)
    }
  }

  const handleRefreshData = () => {
    fetchMetrics()
    fetchPaymentSettings()
    fetchPricingSettings()
    if (showUsersTable) {
      fetchUsers()
    }
  }

  const formatDate = (dateString) => {
    if (!dateString) return "N/A"
    return new Date(dateString).toLocaleString()
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount || 0)
  }

  const getStatusColor = (status) => {
    switch (status) {
      case "verified":
        return "#10b981"
      case "pending":
        return "#f59e0b"
      case "rejected":
        return "#ef4444"
      default:
        return "#6b7280"
    }
  }

  const getStatusIcon = (status) => {
    switch (status) {
      case "verified":
        return faCheck
      case "pending":
        return faClock
      case "rejected":
        return faBan
      default:
        return faReceipt
    }
  }

  return (
    <div className="admin-dashboard">
      <style jsx="true">{`
        .admin-dashboard {
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 2rem;
          background-color: #f9fafb;
          min-height: 100vh;
        }
        
        .dashboard-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          width: 100%;
          max-width: 1200px;
          margin-bottom: 2rem;
        }
        
        .dashboard-title {
          font-size: 2rem;
          font-weight: 700;
          color: #111827;
          margin: 0;
        }
        
        .refresh-button {
          background-color: #007BFF;
          color: white;
          border: none;
          padding: 0.5rem 1rem;
          border-radius: 0.375rem;
          font-weight: 600;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          transition: background-color 0.2s ease;
        }
        
        .refresh-button:hover {
          background-color: #008f85;
        }
        
        .metrics-container {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 1.5rem;
          width: 100%;
          max-width: 1200px;
          margin-bottom: 2rem;
        }
        
        .metric-card {
          background-color: white;
          padding: 1.5rem;
          border-radius: 0.5rem;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          display: flex;
          flex-direction: column;
          align-items: center;
          transition: transform 0.2s ease, box-shadow 0.2s ease;
        }
        
        .metric-card:hover {
          transform: translateY(-3px);
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
        }
        
        .metric-icon {
          font-size: 2rem;
          margin-bottom: 1rem;
          color: #007BFF;
        }
        
        .metric-value {
          font-size: 2.5rem;
          font-weight: 700;
          color: #111827;
          margin-bottom: 0.5rem;
        }
        
        .metric-label {
          font-size: 1rem;
          color: #6b7280;
          text-align: center;
        }
        
        .revenue-value {
          color: #f33a3a;
        }
        
        .transaction-stats {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 1rem;
          width: 100%;
          max-width: 1200px;
          margin-bottom: 2rem;
        }
        
        .stat-card {
          background-color: white;
          padding: 1rem;
          border-radius: 0.5rem;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          text-align: center;
        }
        
        .stat-value {
          font-size: 1.5rem;
          font-weight: 600;
          margin-bottom: 0.5rem;
        }
        
        .stat-label {
          font-size: 0.875rem;
          color: #6b7280;
        }
        
        .quick-actions {
          background-color: white;
          padding: 1.5rem;
          border-radius: 0.5rem;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          width: 100%;
          max-width: 1200px;
          margin-bottom: 2rem;
        }
        
        .quick-actions-title {
          font-size: 1.5rem;
          font-weight: 600;
          color: #111827;
          margin-top: 0;
          margin-bottom: 1.5rem;
        }
        
        .quick-actions-buttons {
          display: flex;
          flex-wrap: wrap;
          gap: 1rem;
        }
        
        .action-button {
          background-color: #007BFF;
          color: white;
          border: none;
          padding: 0.75rem 1.25rem;
          border-radius: 0.375rem;
          font-weight: 600;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          transition: background-color 0.2s ease;
        }
        
        .action-button:hover {
          background-color: #008f85;
        }

        .action-button-link {
          text-decoration: none;
        }

        .action-button-link:hover {
          text-decoration: none;
        }
        
        .manage-users-button, .manage-payment-button {
          background-color: #f33a3a;
          color: white;
          border: none;
          padding: 0.75rem 1.5rem;
          border-radius: 0.375rem;
          font-weight: 600;
          cursor: pointer;
          font-size: 1rem;
          margin-bottom: 1.5rem;
          margin-right: 1rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          transition: background-color 0.2s ease;
        }
        
        .manage-users-button:hover, .manage-payment-button:hover {
          background-color: #e02e2e;
        }
        
        .manage-payment-button {
          background-color: #7c3aed;
        }
        
        .manage-payment-button:hover {
          background-color: #6d28d9;
        }

        .manage-pricing-button {
          background-color: #007BFF;
          color: white;
        }
        .manage-pricing-button:hover {
          background-color: #008f85; 
        }
        
        .payment-settings-container, .pricing-settings-container {
          background-color: white;
          border-radius: 0.5rem;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          width: 100%;
          max-width: 1200px;
          padding: 1.5rem;
          margin-bottom: 2rem;
        }
        
        .payment-settings-header, .pricing-settings-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1.5rem;
        }
        
        .payment-settings-title, .pricing-settings-title {
          font-size: 1.5rem;
          font-weight: 600;
          color: #111827;
          margin: 0;
        }
        
        .edit-button, .save-button, .cancel-button, .upload-button, .remove-button {
          padding: 0.5rem 1rem;
          border-radius: 0.375rem;
          font-weight: 500;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          transition: background-color 0.2s ease;
          border: none;
          margin-left: 0.5rem;
        }
        
        .edit-button {
          background-color: #3b82f6;
          color: white;
        }
        
        .edit-button:hover {
          background-color: #2563eb;
        }
        
        .save-button {
          background-color: #10b981;
          color: white;
        }
        
        .save-button:hover {
          background-color: #059669;
        }
        
        .cancel-button {
          background-color: #ef4444;
          color: white;
        }
        
        .cancel-button:hover {
          background-color: #dc2626;
        }
        
        .upload-button {
          background-color: #8b5cf6;
          color: white;
        }
        
        .upload-button:hover {
          background-color: #7c3aed;
        }
        
        .remove-button {
          background-color: #ef4444;
          color: white;
        }
        
        .remove-button:hover {
          background-color: #dc2626;
        }
        
        .payment-settings-grid { 
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 1.5rem;
        }

        .pricing-plans-grid {
          display: grid;
          gap: 1.5rem;
        }

        .pricing-plan-card {
          border: 1px solid #e5e7eb;
          border-radius: 0.5rem;
          padding: 1.5rem;
          background-color: #f9fafb;
        }

        .plan-name-header {
          font-size: 1.25rem;
          font-weight: 600;
          color: #111827;
          margin-bottom: 1rem;
          text-align: center;
        }

        .pricing-inputs-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1rem;
        }
        
        .setting-item {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }
        
        .setting-label {
          font-weight: 600;
          color: #374151;
        }
        
        .setting-value {
          padding: 0.75rem;
          background-color: #f9fafb;
          border-radius: 0.375rem;
          border: 1px solid #e5e7eb;
          font-family: monospace;
        }
        
        .setting-input {
          padding: 0.75rem;
          border-radius: 0.375rem;
          border: 1px solid #d1d5db;
          font-family: monospace;
        }

        .price-input {
          padding: 0.75rem;
          border-radius: 0.375rem;
          border: 1px solid #d1d5db;
          width: 100%;
          box-sizing: border-box;
        }
        
        .qr-preview {
          width: 200px;
          height: 200px;
          border: 2px solid #e5e7eb;
          border-radius: 0.5rem;
          display: flex;
          align-items: center;
          justify-content: center;
          background-color: #f9fafb;
          color: #6b7280;
          font-size: 0.875rem;
          text-align: center;
          position: relative;
        }
        
        .qr-image {
          width: 100%;
          height: 100%;
          object-fit: contain;
          border-radius: 0.375rem;
        }
        
        .qr-upload-section {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }
        
        .qr-upload-buttons {
          display: flex;
          gap: 0.5rem;
          flex-wrap: wrap;
        }
        
        .file-input {
          display: none;
        }
        
        .users-table-container {
          background-color: white;
          border-radius: 0.5rem;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          width: 100%;
          max-width: 1200px;
          overflow: hidden;
        }
        
        .users-table {
          width: 100%;
          border-collapse: collapse;
        }
        
        .table-header {
          background-color: #f3f4f6;
          color: #374151;
          font-weight: 600;
          text-align: left;
          padding: 1rem;
          border-bottom: 1px solid #e5e7eb;
        }
        
        .table-cell {
          padding: 1rem;
          border-bottom: 1px solid #e5e7eb;
          vertical-align: top;
        }
        
        .table-row:hover {
          background-color: #f9fafb;
        }
        
        .plan-select {
          padding: 0.5rem;
          border-radius: 0.375rem;
          border: 1px solid #d1d5db;
          background-color: white;
          width: 100%;
          max-width: 150px;
        }
        
        .transaction-summary {
          font-size: 0.875rem;
          color: #6b7280;
          margin-bottom: 0.5rem;
        }
        
        .transaction-stats-mini {
          display: flex;
          gap: 1rem;
          margin-bottom: 0.5rem;
        }
        
        .stat-mini {
          display: flex;
          align-items: center;
          gap: 0.25rem;
          font-size: 0.75rem;
        }
        
        .view-transactions-button {
          background-color: #3b82f6;
          color: white;
          border: none;
          padding: 0.5rem 0.75rem;
          border-radius: 0.375rem;
          font-size: 0.875rem;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }
        
        .view-transactions-button:hover {
          background-color: #2563eb;
        }
        
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }
        
        .modal-content {
          background-color: white;
          border-radius: 0.5rem;
          padding: 2rem;
          max-width: 800px;
          width: 90%;
          max-height: 80vh;
          overflow-y: auto;
        }
        
        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1.5rem;
        }
        
        .modal-title {
          font-size: 1.5rem;
          font-weight: 600;
          color: #111827;
          margin: 0;
        }
        
        .close-button {
          background: none;
          border: none;
          font-size: 1.5rem;
          cursor: pointer;
          color: #6b7280;
        }
        
        .close-button:hover {
          color: #374151;
        }
        
        .transaction-item {
          border: 1px solid #e5e7eb;
          border-radius: 0.5rem;
          padding: 1rem;
          margin-bottom: 1rem;
        }
        
        .transaction-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 0.5rem;
        }
        
        .transaction-id {
          font-family: monospace;
          font-weight: 600;
        }
        
        .transaction-status {
          padding: 0.25rem 0.75rem;
          border-radius: 1rem;
          font-size: 0.875rem;
          font-weight: 500;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }
        
        .transaction-details {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
          gap: 1rem;
          margin-bottom: 1rem;
        }
        
        .transaction-detail {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
        }
        
        .detail-label {
          font-size: 0.875rem;
          color: #6b7280;
        }
        
        .detail-value {
          font-weight: 500;
        }
        
        .transaction-actions {
          display: flex;
          gap: 0.5rem;
          flex-wrap: wrap;
        }
        
        .action-btn {
          padding: 0.5rem 1rem;
          border: none;
          border-radius: 0.375rem;
          font-size: 0.875rem;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }
        
        .verify-btn {
          background-color: #10b981;
          color: white;
        }
        
        .verify-btn:hover {
          background-color: #059669;
        }
        
        .reject-btn {
          background-color: #ef4444;
          color: white;
        }
        
        .reject-btn:hover {
          background-color: #dc2626;
        }
        
        .loading-container {
          display: flex;
          justify-content: center;
          align-items: center;
          padding: 2rem;
          color: #6b7280;
        }
        
        .loading-spinner {
          animation: spin 1s linear infinite;
          margin-right: 0.5rem;
          font-size: 1.25rem;
        }
        
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        .error-message {
          background-color: #fee2e2;
          border: 1px solid #fca5a5;
          color: #b91c1c;
          padding: 1rem;
          border-radius: 0.375rem;
          margin-bottom: 1.5rem;
          width: 100%;
          max-width: 1200px;
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }
        
        .success-message {
          background-color: #d1fae5;
          border: 1px solid #6ee7b7;
          color: #047857;
          padding: 1rem;
          border-radius: 0.375rem;
          margin-bottom: 1.5rem;
          width: 100%;
          max-width: 1200px;
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }
        
        .error-icon {
          color: #ef4444;
          font-size: 1.25rem;
        }
        
        .success-icon {
          color: #10b981;
          font-size: 1.25rem;
        }
        
        .no-data-message {
          text-align: center;
          padding: 2rem;
          color: #6b7280;
          font-style: italic;
        }
        
        @media (max-width: 768px) {
          .metrics-container {
            grid-template-columns: 1fr;
          }
          
          .transaction-stats {
            grid-template-columns: repeat(2, 1fr);
          }
          
          .quick-actions-buttons {
            flex-direction: column;
          }
          
          .users-table-container {
            overflow-x: auto;
          }
          
          .payment-settings-grid {
            grid-template-columns: 1fr;
          }

          .pricing-inputs-grid {
            grid-template-columns: 1fr;
          }
          
          .modal-content {
            width: 95%;
            padding: 1rem;
          }
          
          .transaction-details {
            grid-template-columns: 1fr;
          }
        }
      `}</style>

      <div className="dashboard-header">
        <h1 className="dashboard-title">Admin Dashboard</h1>
        <button className="refresh-button" onClick={handleRefreshData}>
          <FontAwesomeIcon icon={faSpinner} className="loading-spinner" />
          Refresh Data
        </button>
      </div>

      {error && (
        <div className="error-message">
          <FontAwesomeIcon icon={faExclamationTriangle} className="error-icon" />
          <span>{error}</span>
        </div>
      )}

      {successMessage && (
        <div className="success-message">
          <FontAwesomeIcon icon={faCheckCircle} className="success-icon" />
          <span>{successMessage}</span>
        </div>
      )}

      {loadingMetrics ? (
        <div className="loading-container">
          <FontAwesomeIcon icon={faSpinner} className="loading-spinner" />
          <span>Loading dashboard metrics...</span>
        </div>
      ) : (
        <>
          <div className="metrics-container">
            <div className="metric-card">
              <FontAwesomeIcon icon={faUsers} className="metric-icon" />
              <div className="metric-value">{metrics.totalUsers}</div>
              <div className="metric-label">Total Users</div>
            </div>

            <div className="metric-card">
              <FontAwesomeIcon icon={faUserCheck} className="metric-icon" />
              <div className="metric-value">{metrics.activeSubscriptions}</div>
              <div className="metric-label">Active Subscriptions</div>
            </div>

            <div className="metric-card">
              <FontAwesomeIcon icon={faUserClock} className="metric-icon" />
              <div className="metric-value">{metrics.trialUsers}</div>
              <div className="metric-label">Trial Users</div>
            </div>

            <div className="metric-card">
              <FontAwesomeIcon icon={faMoneyBillWave} className="metric-icon" style={{ color: "#f33a3a" }} />
              <div className="metric-value revenue-value">{formatCurrency(metrics.monthlyRevenue)}</div>
              <div className="metric-label">Monthly Revenue</div>
            </div>
          </div>

          <div className="transaction-stats">
            <div className="stat-card">
              <div className="stat-value" style={{ color: "#6b7280" }}>
                {metrics.transactionStats.totalTransactions}
              </div>
              <div className="stat-label">Total Transactions</div>
            </div>

            <div className="stat-card">
              <div className="stat-value" style={{ color: "#f59e0b" }}>
                {metrics.transactionStats.pendingTransactions}
              </div>
              <div className="stat-label">Pending</div>
            </div>

            <div className="stat-card">
              <div className="stat-value" style={{ color: "#10b981" }}>
                {metrics.transactionStats.verifiedTransactions}
              </div>
              <div className="stat-label">Verified</div>
            </div>

            <div className="stat-card">
              <div className="stat-value" style={{ color: "#ef4444" }}>
                {metrics.transactionStats.rejectedTransactions}
              </div>
              <div className="stat-label">Rejected</div>
            </div>

            <div className="stat-card">
              <div className="stat-value" style={{ color: "#3b82f6" }}>
                {formatCurrency(metrics.transactionStats.averageAmount)}
              </div>
              <div className="stat-label">Avg Transaction</div>
            </div>
          </div>
        </>
      )}

      <div className="quick-actions">
        <h2 className="quick-actions-title">Quick Actions</h2>
        <div className="quick-actions-buttons">
          <Link to="/admin/create-user" className="action-button-link">
            <button className="action-button">
              <FontAwesomeIcon icon={faUserPlus} />
              Create New User
            </button>
          </Link>
          <button className="action-button manage-pricing-button" onClick={handlePricingSettingsClick}>
            <FontAwesomeIcon icon={faTags} />
            {showPricingSettings ? "Hide Pricing" : "Update Pricing"}
          </button>
        </div>
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: "1rem", marginBottom: "1.5rem" }}>
        <button className="manage-users-button" onClick={handleManageUsersClick}>
          <FontAwesomeIcon icon={faUsers} />
          {showUsersTable ? "Hide Users" : "Manage Users"}
        </button>

        <button className="manage-payment-button" onClick={handlePaymentSettingsClick}>
          <FontAwesomeIcon icon={faQrcode} />
          {showPaymentSettings ? "Hide Payment Settings" : "Manage Payment Settings"}
        </button>
      </div>

      {showPaymentSettings && (
        <div className="payment-settings-container">
          <div className="payment-settings-header">
            <h3 className="payment-settings-title">Payment Settings</h3>
            <div>
              {!editingPaymentSettings ? (
                <button className="edit-button" onClick={handleEditPaymentSettings}>
                  <FontAwesomeIcon icon={faEdit} />
                  Edit Settings
                </button>
              ) : (
                <div style={{ display: "flex" }}>
                  <button className="save-button" onClick={handleSavePaymentSettings} disabled={loadingPaymentSettings}>
                    <FontAwesomeIcon icon={faSave} />
                    {loadingPaymentSettings ? "Saving..." : "Save"}
                  </button>
                  <button className="cancel-button" onClick={handleCancelEditPaymentSettings}>
                    <FontAwesomeIcon icon={faTimes} />
                    Cancel
                  </button>
                </div>
              )}
            </div>
          </div>

          {loadingPaymentSettings ? (
            <div className="loading-container">
              <FontAwesomeIcon icon={faSpinner} className="loading-spinner" />
              <span>Loading payment settings...</span>
            </div>
          ) : (
            <div className="payment-settings-grid">
              <div className="setting-item">
                <label className="setting-label">UPI ID</label>
                {editingPaymentSettings ? (
                  <input
                    type="text"
                    className="setting-input"
                    value={tempPaymentSettings.upiId || ""}
                    onChange={(e) => setTempPaymentSettings((prev) => ({ ...prev, upiId: e.target.value }))}
                    placeholder="Enter UPI ID (e.g., merchant@paytm)"
                  />
                ) : (
                  <div className="setting-value">{paymentSettings.upiId}</div>
                )}
              </div>

              <div className="setting-item">
                <label className="setting-label">
                  <FontAwesomeIcon icon={faCreditCard} style={{ marginRight: "0.5rem", color: "#0070ba" }} />
                  PayPal Client ID
                </label>
                {editingPaymentSettings ? (
                  <input
                    type="text"
                    className="setting-input"
                    value={tempPaymentSettings.paypalClientId || ""}
                    onChange={(e) => setTempPaymentSettings((prev) => ({ ...prev, paypalClientId: e.target.value }))}
                    placeholder="Enter PayPal Client ID (e.g., AfjkWpPl3hTHFtqDGsS3RoDoQfQjVvWk...)"
                  />
                ) : (
                  <div className="setting-value">
                    {paymentSettings.paypalClientId
                      ? `${paymentSettings.paypalClientId.substring(0, 20)}...`
                      : "Not configured"}
                  </div>
                )}
                <p style={{ fontSize: "0.75rem", color: "#6b7280", marginTop: "0.25rem" }}>
                  {editingPaymentSettings
                    ? "Enter your PayPal Client ID from your PayPal Developer Dashboard"
                    : "PayPal Client ID for processing international payments"}
                </p>
              </div>

              <div className="setting-item">
                <label className="setting-label">Status</label>
                {editingPaymentSettings ? (
                  <select
                    className="setting-input"
                    value={tempPaymentSettings.isActive ? "active" : "inactive"}
                    onChange={(e) =>
                      setTempPaymentSettings((prev) => ({ ...prev, isActive: e.target.value === "active" }))
                    }
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                ) : (
                  <div className="setting-value" style={{ color: paymentSettings.isActive ? "#10b981" : "#ef4444" }}>
                    {paymentSettings.isActive ? "Active" : "Inactive"}
                  </div>
                )}
              </div>

              <div className="setting-item">
                <label className="setting-label">QR Code Image</label>
                <div className="qr-upload-section">
                  <div className="qr-preview">
                    {paymentSettings.qrCodeUrl ? (
                      <img
                        src={paymentSettings.qrCodeUrl || "/placeholder.svg"}
                        alt="QR Code"
                        className="qr-image"
                        onError={(e) => {
                          e.target.style.display = "none"
                          e.target.nextSibling.style.display = "block"
                        }}
                      />
                    ) : null}
                    <div style={{ display: paymentSettings.qrCodeUrl ? "none" : "block" }}>
                      No QR Code Image
                      <br />
                      {editingPaymentSettings ? "Upload an image below" : "Click Edit to upload"}
                    </div>
                  </div>

                  {editingPaymentSettings && (
                    <>
                      <div className="qr-upload-buttons">
                        <input
                          type="file"
                          id="qr-upload"
                          className="file-input"
                          accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                          onChange={handleQRCodeUpload}
                        />
                        <label htmlFor="qr-upload" className="upload-button">
                          <FontAwesomeIcon icon={faUpload} />
                          {uploadingQR ? "Uploading..." : "Upload QR Code"}
                        </label>

                        {paymentSettings.qrCodeUrl && (
                          <button
                            className="remove-button"
                            onClick={handleRemoveQRCode}
                            disabled={loadingPaymentSettings}
                          >
                            <FontAwesomeIcon icon={faTrash} />
                            Remove QR Code
                          </button>
                        )}
                      </div>

                      <p style={{ fontSize: "0.875rem", color: "#6b7280", margin: 0 }}>
                        Supported formats: JPEG, PNG, GIF, WebP (Max size: 5MB)
                      </p>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Pricing Settings Section */}
      {showPricingSettings && (
        <div className="pricing-settings-container">
          <div className="pricing-settings-header">
            <h3 className="pricing-settings-title">Pricing Settings</h3>
            <div>
              {!editingPricingSettings ? (
                <button className="edit-button" onClick={handleEditPricingSettings}>
                  <FontAwesomeIcon icon={faEdit} />
                  Edit Prices
                </button>
              ) : (
                <div style={{ display: "flex" }}>
                  <button className="save-button" onClick={handleSavePricingSettings} disabled={loadingPricingSettings}>
                    <FontAwesomeIcon icon={faSave} />
                    {loadingPricingSettings ? "Saving..." : "Save"}
                  </button>
                  <button className="cancel-button" onClick={handleCancelEditPricingSettings}>
                    <FontAwesomeIcon icon={faTimes} />
                    Cancel
                  </button>
                </div>
              )}
            </div>
          </div>

          {loadingPricingSettings ? (
            <div className="loading-container">
              <FontAwesomeIcon icon={faSpinner} className="loading-spinner" />
              <span>Loading pricing settings...</span>
            </div>
          ) : (
            <div className="pricing-plans-grid">
              {(editingPricingSettings ? tempPricingSettings : pricingSettings).map((plan, index) => (
                <div key={plan.name} className="pricing-plan-card">
                  <div className="plan-name-header">{plan.name}</div>
                  <div className="pricing-inputs-grid">
                    <div className="setting-item">
                      <label className="setting-label">Monthly Price ($)</label>
                      {editingPricingSettings ? (
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          className="price-input"
                          value={plan.price?.monthly || ""}
                          onChange={(e) => handlePriceChange(index, "monthly", e.target.value)}
                          placeholder="0.00"
                        />
                      ) : (
                        <div className="setting-value">{formatCurrency(plan.price?.monthly || 0)}</div>
                      )}
                    </div>
                    <div className="setting-item">
                      <label className="setting-label">Yearly Price ($)</label>
                      {editingPricingSettings ? (
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          className="price-input"
                          value={plan.price?.yearly || ""}
                          onChange={(e) => handlePriceChange(index, "yearly", e.target.value)}
                          placeholder="0.00"
                        />
                      ) : (
                        <div className="setting-value">{formatCurrency(plan.price?.yearly || 0)}</div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {showUsersTable && (
        <div className="users-table-container">
          {loadingUsers ? (
            <div className="loading-container">
              <FontAwesomeIcon icon={faSpinner} className="loading-spinner" />
              <span>Loading Users Data...</span>
            </div>
          ) : usersData.length === 0 ? (
            <div className="no-data-message">No users found.</div>
          ) : (
            <table className="users-table">
              <thead>
                <tr>
                  <th className="table-header">User Info</th>
                  <th className="table-header">Plan Type</th>
                  <th className="table-header">Transaction Summary</th>
                  <th className="table-header">Upgrade Plan</th>
                  <th className="table-header">Actions</th>
                </tr>
              </thead>
              <tbody>
                {usersData.map((user) => (
                  <tr key={user.id} className="table-row">
                    <td className="table-cell">
                      <div>
                        <div style={{ fontWeight: "600", marginBottom: "0.25rem" }}>{user.name}</div>
                        <div style={{ fontSize: "0.875rem", color: "#6b7280" }}>{user.email}</div>
                        <div style={{ fontSize: "0.75rem", color: "#9ca3af" }}>Role: {user.role}</div>
                      </div>
                    </td>
                    <td className="table-cell">
                      <div style={{ fontWeight: "500" }}>{user.plan_type || "Free"}</div>
                    </td>
                    <td className="table-cell">
                      <div className="transaction-summary">
                        <div className="transaction-stats-mini">
                          <div className="stat-mini">
                            <FontAwesomeIcon icon={faReceipt} style={{ color: "#6b7280" }} />
                            <span>{user.totalTransactions || 0} total</span>
                          </div>
                          <div className="stat-mini">
                            <FontAwesomeIcon icon={faClock} style={{ color: "#f59e0b" }} />
                            <span>{user.pendingTransactions || 0} pending</span>
                          </div>
                          <div className="stat-mini">
                            <FontAwesomeIcon icon={faCheck} style={{ color: "#10b981" }} />
                            <span>{user.verifiedTransactions || 0} verified</span>
                          </div>
                        </div>
                        <div style={{ fontSize: "0.875rem", fontWeight: "500", color: "#10b981" }}>
                          Total Paid: {formatCurrency(user.totalPaid || 0)}
                        </div>
                        {user.lastTransaction && (
                          <div style={{ fontSize: "0.75rem", color: "#6b7280", marginTop: "0.25rem" }}>
                            Last: {formatDate(user.lastTransaction.submitted_at)}
                          </div>
                        )}
                        {user.totalTransactions > 0 && (
                          <button
                            className="view-transactions-button"
                            onClick={() => handleViewTransactions(user)}
                            style={{ marginTop: "0.5rem" }}
                          >
                            <FontAwesomeIcon icon={faEye} />
                            View Transactions
                          </button>
                        )}
                      </div>
                    </td>
                    <td className="table-cell">
                      <select
                        className="plan-select"
                        onChange={(e) => handleUpgradePlanChange(user.id, e.target.value)}
                        value={user.plan_type || ""}
                        disabled={actionLoading[user.id]}
                      >
                        <option value="">Free</option>
                        <option value="trial">Trial</option>
                        <option value="professional">Professional</option>
                        <option value="business">Business</option>
                      </select>
                    </td>
                    <td className="table-cell">
                      <button
                        className="cancel-button"
                        onClick={() => handleCancelSubscriptionClick(user.id)}
                        disabled={actionLoading[user.id] || !user.plan_type}
                      >
                        {actionLoading[user.id] ? "Processing..." : "Cancel Plan"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Transaction Modal */}
      {showTransactionModal && selectedUserTransactions && (
        <div className="modal-overlay" onClick={() => setShowTransactionModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Transactions for {selectedUserTransactions.name}</h3>
              <button className="close-button" onClick={() => setShowTransactionModal(false)}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            {selectedUserTransactions.transactions.length === 0 ? (
              <div className="no-data-message">No transactions found for this user.</div>
            ) : (
              <div>
                {selectedUserTransactions.transactions.map((transaction) => (
                  <div key={transaction.id} className="transaction-item">
                    <div className="transaction-header">
                      <div className="transaction-id">#{transaction.transaction_id}</div>
                      <div
                        className="transaction-status"
                        style={{
                          backgroundColor: `${getStatusColor(transaction.status)}20`,
                          color: getStatusColor(transaction.status),
                        }}
                      >
                        <FontAwesomeIcon icon={getStatusIcon(transaction.status)} />
                        {transaction.status.charAt(0).toUpperCase() + transaction.status.slice(1)}
                      </div>
                    </div>

                    <div className="transaction-details">
                      <div className="transaction-detail">
                        <div className="detail-label">Amount</div>
                        <div className="detail-value">{formatCurrency(transaction.amount)}</div>
                      </div>
                      <div className="transaction-detail">
                        <div className="detail-label">Plan</div>
                        <div className="detail-value">{transaction.plan_type || "N/A"}</div>
                      </div>
                      <div className="transaction-detail">
                        <div className="detail-label">Submitted</div>
                        <div className="detail-value">{formatDate(transaction.submitted_at)}</div>
                      </div>
                    </div>

                    {/* Only show action buttons for pending transactions */}
                    {transaction.status === "pending" && (
                      <div className="transaction-actions">
                        <button
                          className="action-btn verify-btn"
                          onClick={() => handleUpdateTransactionStatus(transaction.id, "verified", "Verified by admin")}
                          disabled={updatingTransaction}
                        >
                          <FontAwesomeIcon icon={faCheck} />
                          {updatingTransaction ? "Updating..." : "Verify"}
                        </button>
                        <button
                          className="action-btn reject-btn"
                          onClick={() => handleUpdateTransactionStatus(transaction.id, "rejected", "Rejected by admin")}
                          disabled={updatingTransaction}
                        >
                          <FontAwesomeIcon icon={faBan} />
                          {updatingTransaction ? "Updating..." : "Reject"}
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default AdminDashboard
